export type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function getStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function readCache<T>(key: string): T | null {
  const storage = getStorage();
  if (!storage) return null;

  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (Date.now() > parsed.expiresAt) {
      storage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch (e) {
    console.error("[cache] Failed to parse entry", key, e);
    storage.removeItem(key);
    return null;
  }
}

export function writeCache<T>(key: string, value: T, ttlMs: number = DEFAULT_TTL_MS) {
  const storage = getStorage();
  if (!storage) return;

  const payload: CacheEntry<T> = {
    value,
    expiresAt: Date.now() + ttlMs,
  };

  storage.setItem(key, JSON.stringify(payload));
}

export function invalidateCache(key: string) {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(key);
}

export function invalidateByPrefix(prefix: string) {
  const storage = getStorage();
  if (!storage) return;
  const keysToDelete: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) keysToDelete.push(k);
  }
  keysToDelete.forEach((k) => storage.removeItem(k));
}
